import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import edjsHTML from 'editorjs-html';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useNewsletters } from 'hooks/useNewsletters.hook';
import { useNotification } from 'hooks/useNotification';
import { ImagesAttributes } from 'lib/models/newsletter';
import { PAGE_ROUTES } from 'lib/page-routes';
import { API_ROUTES } from 'lib/api-routes';
import { apiInstance } from 'lib/utils/axios';
import { BasicOpportunity } from 'lib/models/opportunity';
import { TouchpointAttributes, TouchpointType } from 'lib/models/touchpoint';
import { useSocietyGlobal } from 'lib/contexts/society-global';
import ButtonTool from './utils/ButtonTool';
import buttonParserConfig from './utils/buttonParser';
import OpportunityCardTool from './utils/OpportunityCardTool';
import { opportunityCardParser } from './utils/OpportunityCardParser';

import styles from './Editor.module.scss';

enum OpportunityCardType {
  JOBS = 'Jobs',
  INTERNSHIPS = 'Internships',
}

enum BlockParserType {
  OPPORTUNITY = 'opportunityCard',
  BUTTON = 'button',
}
interface ImageData {
  data: {
    file?: {
      url: string;
    };
    url?: string;
    caption?: string;
    withBorder?: boolean;
    withBackground?: boolean;
    stretched?: boolean;
  };
}

const imageParserConfig = {
  image: (data: ImageData) => {
    if (!data || typeof data !== 'object') {
      console.error('Invalid image data:', data);
      return '';
    }
    const { file, url, caption, withBorder, withBackground } = data.data;
    const imageUrl = (file && file.url) || url || '';
    if (!imageUrl) {
      console.error('No valid image URL found:', data);
      return '';
    }

    const imageWrapperStyle = `
    ${withBorder ? 'border: 1px solid #e8e8eb;' : ''}
    ${withBackground ? 'background: #cdd1e0; padding: 15px;' : ''}
  `;
    return `
      <div class="image-tool">
        <div style="${imageWrapperStyle}">
          <img src="${imageUrl}" alt="${caption || ''}" />
        </div>
        ${caption ? `<div class="image-tool__caption">${caption}</div>` : ''}
      </div>
    `;
  },
};

const edjsParser = edjsHTML({
  button: buttonParserConfig,
  image: imageParserConfig.image,

  opportunityCard: (data: {
    data: { opportunityAttributes: BasicOpportunity[]; type: string };
  }) => {
    const { opportunityAttributes, type } = data.data;
    return opportunityCardParser(opportunityAttributes, type);
  },
});

export interface EditorProps {
  className?: string;
  style?: Record<string, unknown>;
  newsLetterResponse?: {
    id: string;
    subject?: string | undefined;
    letter?: string | undefined;
  };
  letter?: string;
  onUpdateLoading?: (loading: boolean, saved: boolean) => void;
  newsletterImage?: {
    id: string;
    type: string;
    attributes: ImagesAttributes;
  }[];
  touchpointIds?: string[];
  onPreviewClick?: boolean;
  setEditorHtml: (html: string) => void;
}

export const Editor: React.FC<EditorProps> = memo(
  ({
    newsLetterResponse,
    letter,
    onUpdateLoading,
    setEditorHtml,
    touchpointIds,
  }: EditorProps) => {
    const { updateNewsletter, deleteImage, uploadImage, onDelete } =
      useNewsletters();
    const { setSelectedTouchpoints, getCopiedLink } = useSocietyGlobal();
    const notificationInstance = useNotification();
    const router = useRouter();
    const handleRedirect = () => {
      router.push(PAGE_ROUTES.CAREER_OPPORTUNITY);
    };
    const INITIAL_DATA = letter
      ? JSON.parse(letter)
      : {
          time: new Date().getTime(),
          blocks: [
            {
              type: 'paragraph',
              data: {
                text: '',
                level: 1,
              },
            },
          ],
        };

    const handleImageUpload = async (file: File) => {
      if (!newsLetterResponse) {
        return {
          success: 0,
          file: {
            url: '',
          },
        };
      }
      try {
        const formData = new FormData();
        formData.append('image', file);

        const data = await uploadImage(newsLetterResponse?.id, formData);
        return {
          success: 1,
          file: {
            id: data.data.attributes.id,
            url: data.data.attributes.url,
          },
        };
      } catch (error) {
        return {
          success: 0,
          file: {
            url: '',
          },
        };
      }
    };
    const convertEmailHtml = (html: string) => {
      return `<div style="max-width:800px;margin: 0 auto;">${html}</div>`.replace(
        /<img\s+([^>]*)>/g,
        '<img $1 style="max-width:800px" >'
      );
    };

    const ejInstance = useRef<EditorJS | null>(null);
    const initEditor = async () => {
      if (ejInstance.current === null) {
        const EditorJS = (await import('@editorjs/editorjs')).default;
        const Image = (await import('@editorjs/image')).default;
        // @ts-ignore
        const Underline = (await import('@editorjs/underline')).default;
        const editor = new EditorJS({
          onReady: async () => {
            const toolbarDropDown = document.querySelector(
              '.ce-inline-toolbar__dropdown'
            ) as HTMLElement;
            if (toolbarDropDown) {
              toolbarDropDown.remove();
            }

            const content = await editor.saver.save();

            const hasOpportunityCard = content.blocks.some(
              (block: { type: string }) =>
                block.type === BlockParserType.OPPORTUNITY
            );

            // Only call addOpportunityCardBlock if the opportunity card is not present
            if (
              touchpointIds &&
              touchpointIds.length > 0 &&
              !hasOpportunityCard
            ) {
              await addOpportunityCardBlock(editor);
            }

            // Hide the Opportunity Card tool from the toolbar
            const tool = document.querySelector(
              '[data-item-name="opportunityCard"]'
            ) as HTMLElement;
            if (tool) {
              tool.style.display = 'none';
            }
          },
          holder: 'editorjs',
          data: INITIAL_DATA,
          tools: {
            paragraph: {
              config: {
                placeholder: 'Begin writing your newsletter...',
              },
            },
            header: Header,
            list: List,
            underline: Underline,
            image: {
              class: Image,
              config: {
                uploader: {
                  uploadByFile(file: File) {
                    return handleImageUpload(file);
                  },
                },
              },
            },
            button: ButtonTool,
            opportunityCard: {
              //@ts-ignore
              class: OpportunityCardTool,
              config: {
                onRedirect: handleRedirect, // Pass the redirect function
              },
            },
          },
          onChange: async () => {
            if (editor.saver) {
              const content = await editor.saver.save();
              const html = edjsParser.parse(content).join('');
              const emailHtml = html ? convertEmailHtml(html) : '';
              onUpdateLoading && onUpdateLoading(true, false);
              setEditorHtml(emailHtml);
              const res = await updateNewsletter({
                news_letter: {
                  letter: JSON.stringify(content),
                  letter_html: emailHtml,
                },
                id: newsLetterResponse?.id || '',
              });

              if (res) {
                if (!res.letter_html && !res.subject) {
                  await onDelete(res.id as string, false);
                  router.push(`${PAGE_ROUTES.NEWSLETTER_CREATE}?redirect=home`);
                }
                handleDeleteImage(res?.images?.data);
              }
              setTimeout(
                () => onUpdateLoading && onUpdateLoading(false, true),
                400
              );
              setTimeout(
                () => onUpdateLoading && onUpdateLoading(false, false),
                1400
              );
            }
          },
        });

        ejInstance.current = editor;
      }
    };

    const [disabled, setDisabled] = useState<boolean>(false);

    const addOpportunityCardBlock = async (editor: EditorJS) => {
      const touchpoints = await getTouchPoints();

      if (touchpoints.length > 0) {
        const jobTouchpoints = touchpoints.filter(
          (touchpoint: TouchpointAttributes) =>
            touchpoint.touchpointable_type === TouchpointType.Job
        );
        const internshipTouchpoints = touchpoints.filter(
          (touchpoint: TouchpointAttributes) =>
            touchpoint.touchpointable_type === TouchpointType.Internship
        );

        if (jobTouchpoints.length > 0 && internshipTouchpoints.length > 0) {
          editor.blocks.insert(
            BlockParserType.OPPORTUNITY,
            {
              opportunityAttributes: jobTouchpoints.slice(0, 5),
              type: OpportunityCardType.JOBS,
              touchpointIds: touchpointIds,
            },
            {},
            editor.blocks.getBlocksCount() - 1
          );
          if (jobTouchpoints && jobTouchpoints.length > 5) {
            const showAllBlockData = {
              text: `View ${jobTouchpoints.length} jobs`,
              link: await getCopiedLink(TouchpointType.Job),
              programmaticallyAdded: true,
            };

            editor.blocks.insert(BlockParserType.BUTTON, showAllBlockData, {});
          }

          editor.blocks.insert(
            'opportunityCard',
            {
              opportunityAttributes: internshipTouchpoints.slice(0, 5),
              type: OpportunityCardType.INTERNSHIPS,
              touchpointIds: touchpointIds,
            },
            {},
            editor.blocks.getBlocksCount() - 1
          );
          if (internshipTouchpoints && internshipTouchpoints.length > 5) {
            const showAllBlockData = {
              text: `View ${internshipTouchpoints.length} Internships`,
              link: await getCopiedLink(TouchpointType.Internship),
              programmaticallyAdded: true,
            };

            editor.blocks.insert(BlockParserType.BUTTON, showAllBlockData, {});
          }
        } else {
          const blockData = {
            opportunityAttributes: touchpoints?.slice(0, 5),
            touchpointIds: touchpointIds,
          };
          const touchpointType = touchpoints.some(
            (touchpoint: TouchpointAttributes) =>
              touchpoint.touchpointable_type === TouchpointType.Job
          )
            ? TouchpointType.Job
            : TouchpointType.Internship;

          editor.blocks.insert(
            BlockParserType.OPPORTUNITY,
            blockData,
            {},
            editor.blocks.getBlocksCount() - 1
          );

          if (touchpointIds && touchpointIds.length > 5) {
            const showAllBlockData = {
              text: `View ${touchpointIds.length} ${
                touchpointType === TouchpointType.Job ? 'Jobs' : 'Internships'
              }`,
              link: await getCopiedLink(touchpointType),
              programmaticallyAdded: true,
            };

            editor.blocks.insert(BlockParserType.BUTTON, showAllBlockData, {});
          }
        }
      }
    };

    const getTouchPoints = async () => {
      const queryString = touchpointIds
        ? `ids[]=${touchpointIds.join('&ids[]=')}&per_page=${
            touchpointIds.length
          }`
        : '';

      const url = `${API_ROUTES.BASIC_TOUCHPOINTS_V2}?${queryString}`;
      try {
        const res = await apiInstance.get(url);
        setSelectedTouchpoints(res.data.data);
        return res.data.data;
      } catch (err) {
        notificationInstance.handleExceptionError(err);
      }
    };

    useEffect(() => {
      if (touchpointIds && touchpointIds?.length > 0) getTouchPoints();
    }, [touchpointIds]);

    const handleDeleteImage = (
      uploadedImage: {
        id: string;
        type: string;
        attributes: ImagesAttributes;
      }[]
    ) => {
      try {
        const currentImages: string[] = [];
        document
          .querySelectorAll('.image-tool__image-picture')
          .forEach((img: { id?: string; src?: string }) => {
            const imageUrl = img.src;
            currentImages.push(imageUrl || '');
          });
        if (uploadedImage && uploadedImage?.length > currentImages.length) {
          uploadedImage.forEach(
            async (img: { attributes: ImagesAttributes }) => {
              if (!currentImages.includes(img?.attributes.url)) {
                deleteImage(newsLetterResponse?.id || '', img);
              }
            }
          );
        }
      } catch (error) {
        console.error(`Error handling image deletion: ${error}`);
      }
    };

    useEffect(() => {
      if (newsLetterResponse && ejInstance.current === null) {
        initEditor();
      } else {
        setDisabled(true);
      }
      return () => {
        ejInstance?.current?.destroy();
        ejInstance.current = null;
      };
    }, [letter]);

    if (disabled) {
      return (
        <div className={styles.disabledEditor}>
          Begin writing your newsletter...
        </div>
      );
    }

    return <div className={styles.newsletterContent} id="editorjs"></div>;
  }
);

Editor.displayName = 'Editor';
